import { useQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import topicIcon from '../../icons/prefilled/topic.svg';
import TopicDetailPreviewPopOver from '../../topic/TopicDetailPreviewPopOver';
import TopicPrivacyIcon from '../../topic/TopicPrivacyIcon';
import topicPath, { publicTopicPath } from '../../utils/url/topicPath';
import useIsPublicView from '../../utils/useIsPublicView';
import InaccessibleMentionHoverCard from './InaccessibleMentionHoverCard';
import MentionBase from './MentionBase';
import { gql } from '../../graphql/generated/gql';
import { TopicPrivacy } from '../../graphql/generated/graphql';

const TOPIC_QUERY = gql(`
  query TopicMention($id: ID!) {
    topic(id: $id) {
      id
      name
      privacy
      memberEditable
    }
  }
`);

const PUBLIC_TOPIC_QUERY = gql(`
  query TopicMentionPublic($id: ID!) {
    topic: publicTopic(id: $id) {
      id
      name
      privacy
    }
  }
`);

interface TopicMentionProps {
  id: string;
  content: string | null;
  mentionText?: string | null;
  updateCache?: (name?: string) => void;
  link: boolean;
  showHoverCard?: boolean;
  selected?: boolean;
  singleLine?: boolean;
  newTab?: boolean;
}

const TopicMention = ({
  id,
  content,
  mentionText,
  updateCache,
  link,
  showHoverCard,
  selected,
  singleLine,
  newTab,
}: TopicMentionProps) => {
  const isPublicView = useIsPublicView();

  const { data: publicData, loading: isPublicLoading } = useQuery(
    PUBLIC_TOPIC_QUERY,
    { variables: { id }, skip: !isPublicView },
  );

  const { data, loading: isPrivateLoading } = useQuery(TOPIC_QUERY, {
    variables: { id },
    skip: isPublicView,
  });

  const isLoading = isPublicLoading || isPrivateLoading;
  const topic = isPublicView ? publicData?.topic : data?.topic;
  const isPublic = topic?.privacy === TopicPrivacy.Public;

  const path =
    isPublic && isPublicView
      ? publicTopicPath(topic ?? { id, name: content ?? '' })
      : topicPath(topic ?? { id, name: content ?? '' });

  useEffect(() => {
    if (!isPublicView && topic && updateCache) {
      updateCache(topic.name);
    }
  }, [updateCache, isPublicView, topic]);

  const commonProps = {
    icon: topicIcon,
    type: 'topic',
    truncate: true,
    to: link ? path : undefined,
    content: mentionText ?? topic?.name ?? content ?? undefined,
    iconAfter: topic && <TopicPrivacyIcon topic={topic} iconSize="medium" />,
    singleLine,
    newTab,
  };

  const mention = <MentionBase {...commonProps} />;

  if (!showHoverCard || isLoading || selected) {
    return mention;
  }

  if (!topic) {
    return (
      <MentionBase
        {...commonProps}
        popOver={<InaccessibleMentionHoverCard mention="topic" />}
      />
    );
  }

  return (
    <TopicDetailPreviewPopOver
      allowCrossAxisFlip
      display="inline"
      topicId={topic.id}
    >
      {mention}
    </TopicDetailPreviewPopOver>
  );
};

export default TopicMention;
